import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { agentsApi, clientsApi } from '../services/api';
import './AgentsList.css';

const AgentsList = () => {
  const [agents, setAgents] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedClient, setSelectedClient] = useState('');

  // Load agents and clients on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Get clients first
        const clientsResponse = await clientsApi.getClients();
        setClients(clientsResponse.clients || []);
        
        // Then fetch agents (initially all)
        const agentsResponse = await agentsApi.getAgents();
        setAgents(agentsResponse.agents || []);
        
        setError('');
      } catch (err) {
        setError('Failed to load data. Please try again.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  // Filter agents by client
  const handleClientChange = async (e) => {
    const clientId = e.target.value;
    setSelectedClient(clientId);
    
    try {
      setLoading(true);
      
      // Fetch filtered agents
      const response = await agentsApi.getAgents(clientId || null);
      setAgents(response.agents || []);
      
      setError('');
    } catch (err) {
      setError('Failed to filter agents. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Delete an agent
  const handleDeleteAgent = async (agentId) => {
    if (!window.confirm('Are you sure you want to delete this agent?')) {
      return;
    }
    
    try {
      setLoading(true);
      
      // Delete agent
      await agentsApi.deleteAgent(agentId);
      
      // Refresh agents list
      const response = await agentsApi.getAgents(selectedClient || null);
      setAgents(response.agents || []);
      
      setError('');
    } catch (err) {
      setError('Failed to delete agent. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Find client name by ID
  const getClientName = (clientId) => {
    const client = clients.find(client => client.id === clientId);
    return client ? client.name : 'Unknown';
  };

  return (
    <div>
      <div className="header">
        <h1>Voice Agents</h1>
        <Link to="/agents/new" className="btn">Create New Agent</Link>
      </div>
      
      {error && <div className="error-message">{error}</div>}
      
      <div className="card">
        <div className="filter-section">
          <label htmlFor="client-filter">Filter by Client:</label>
          <select
            id="client-filter"
            value={selectedClient}
            onChange={handleClientChange}
            className="form-control"
          >
            <option value="">All Clients</option>
            {clients.map(client => (
              <option key={client.id} value={client.id}>{client.name}</option>
            ))}
          </select>
        </div>
        
        {loading ? (
          <div className="loading">Loading agents...</div>
        ) : agents.length === 0 ? (
          <div className="empty-state">
            <p>No agents found.</p>
            <Link to="/agents/new" className="btn">Create Your First Agent</Link>
          </div>
        ) : (
          <div className="agents-list">
            <table className="table">
              <thead>
                <tr>
                  <th>Agent Name</th>
                  <th>Client</th>
                  <th>ElevenLabs ID</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {agents.map(agent => (
                  <tr key={agent.id}>
                    <td>{agent.name}</td>
                    <td>{getClientName(agent.client_id)}</td>
                    <td className="id-cell">
                      {agent.elevenlabs_agent_id}
                      <button
                        className="btn-icon copy-icon"
                        onClick={() => {
                          navigator.clipboard.writeText(agent.elevenlabs_agent_id);
                          alert('Agent ID copied to clipboard!');
                        }}
                        title="Copy Agent ID"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="copy-svg">
                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                          <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"></path>
                        </svg>
                      </button>
                    </td>
                    <td>{new Date(agent.created_at).toLocaleDateString()}</td>
                    <td className="actions-cell">
                      <Link
                        to={`/data?agent=${agent.id}`}
                        className="btn-action view"
                        title="View Data"
                      >
                        View Data
                      </Link>
                      <a
                        href={`https://agentes.vittae360.com/home?agentid=${agent.elevenlabs_agent_id}`} 
                        className="btn-action open"
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Open Agent"
                      >
                        Open Agent
                      </a>
                      <button
                        className="btn-action delete"
                        onClick={() => handleDeleteAgent(agent.id)}
                        title="Delete Agent"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AgentsList;
